import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, CheckCircle2, ShieldCheck, CreditCard, Zap, Star } from 'lucide-react';
import { PlanSelector, PlanId } from './PlanSelector';
import { UrgencyBanner } from './UrgencyBanner';
interface OfferProps {
  selectedPlan: PlanId;
  onSelectPlan: (id: PlanId) => void;
}
export function Offer({
  selectedPlan,
  onSelectPlan
}: OfferProps) {
  const comparisons = [{
    bad: '40 minutos por primera consulta explicando desde cero',
    good: '20 minutos con guión y plantilla por tumor'
  }, {
    bad: 'El paciente firma sin entender y después dice "nadie me avisó"',
    good: 'Consentimiento en lenguaje llano que protege a los dos'
  }, {
    bad: 'Llamadas fuera de horario por síntomas esperados',
    good: 'Semáforo de síntomas: el paciente sabe qué es normal y qué es urgencia'
  }, {
    bad: 'La pregunta del pronóstico te agarra sin protocolo',
    good: 'Respuesta estructurada en rangos, con honestidad y sin cifras grabadas a fuego'
  }, {
    bad: 'El pacto de silencio se resuelve mintiendo o chocando con la familia',
    good: 'Protocolo de salida en 4 pasos con respaldo legal'
  }, {
    bad: 'La primera imagen post-RT genera pánico y consultas a otros centros',
    good: 'Respuesta diferida anticipada desde la consulta de alta'
  }, {
    bad: 'El control semanal arranca con 10 minutos de relato desordenado',
    good: 'Hoja de registro: leés la semana del paciente en 60 segundos'
  }, {
    bad: 'Cada residente comunica como puede',
    good: 'Todo el equipo con el mismo sistema y las mismas frases'
  }];
  return <div className="w-full" id="pricing" data-id="element-75">
      {/* Comparison Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-50" data-id="element-76">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-77">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-78">
            <span className="text-slate-500 font-bold tracking-wider uppercase text-xs sm:text-sm mb-2 block" data-id="element-79">
              LA REALIDAD
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 sm:mb-4" data-id="element-80">
              ¿Cuánto cuesta NO tenerlo?
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-8 sm:mb-12" data-id="element-81">
            <div className="hidden sm:grid sm:grid-cols-2 bg-slate-100 border-b border-slate-200" data-id="element-82">
              <div className="p-4 sm:p-6 text-center font-bold text-slate-500" data-id="element-83">
                Sin el sistema
              </div>
              <div className="p-4 sm:p-6 text-center font-bold text-teal-700 bg-teal-50" data-id="element-84">
                Con el sistema
              </div>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-85">
              {comparisons.map((row, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-20px'
            }} transition={{
              delay: idx * 0.05
            }} className="flex flex-col sm:grid sm:grid-cols-2" data-id="element-86">
                  <div className="p-4 sm:p-6 flex items-start gap-3 bg-red-50/30 border-b sm:border-b-0 border-slate-100" data-id="element-87">
                    <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 shrink-0 mt-0.5" data-id="element-88" />
                    <span className="text-slate-700 text-sm sm:text-base" data-id="element-89">
                      {row.bad}
                    </span>
                  </div>
                  <div className="p-4 sm:p-6 flex items-start gap-3 bg-teal-50/50" data-id="element-90">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-teal-500 shrink-0 mt-0.5" data-id="element-91" />
                    <span className="text-slate-900 font-medium text-sm sm:text-base" data-id="element-92">
                      {row.good}
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-900 text-white" data-id="element-93">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-94">
          <div className="mb-8 sm:mb-10" data-id="element-95">
            <UrgencyBanner data-id="element-96" />
          </div>
          <div className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-97">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-3 sm:mb-4" data-id="element-98">
              Elegí tu Kit
            </h2>
            <p className="text-lg sm:text-xl text-teal-400" data-id="element-99">
              Ambas opciones incluyen los 3 bonos completos
            </p>
          </div>

          <div className="mb-10 sm:mb-12" data-id="element-100">
            <PlanSelector selected={selectedPlan} onSelect={onSelectPlan} ctaId="comprar-megapack" data-id="element-101" />
          </div>

          <div className="text-center max-w-2xl mx-auto" data-id="element-102">
            <p className="text-sm sm:text-base text-slate-300 mb-4 sm:mb-6 font-medium" data-id="element-103">
              La opción más elegida por radioterapeutas que quieren el sistema
              de comunicación completo, del adulto al niño y de la técnica
              convencional a la alta precisión
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-400 font-medium" data-id="element-104">
              <div className="flex items-center gap-1.5" data-id="element-105">
                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-400" data-id="element-106" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center gap-1.5" data-id="element-107">
                <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-400" data-id="element-108" />{' '}
                Acceso en 5 min
              </div>
              <div className="flex items-center gap-1.5" data-id="element-109">
                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-400" data-id="element-110" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center gap-1.5" data-id="element-111">
                <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-400" data-id="element-112" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Megapack Details Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white border-b border-slate-100" data-id="element-113">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-114">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-115">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 sm:mb-4" data-id="element-116">
              ¿QUÉ AGREGA EL MEGAPACK?
            </h2>
            <p className="text-lg sm:text-xl text-slate-600" data-id="element-117">
              Para radioterapeutas que quieren la especialidad completa, no solo
              el tratamiento convencional
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 mb-8 sm:mb-12 lg:mb-16" data-id="element-118">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-slate-50 p-5 sm:p-8 rounded-2xl border border-slate-200" data-id="element-119">
              <h3 className="font-bold text-slate-500 mb-3 sm:mb-4 uppercase tracking-wider text-xs sm:text-sm" data-id="element-120">
                Sin Megapack
              </h3>
              <p className="text-sm sm:text-base text-slate-700" data-id="element-121">
                Sabés comunicar con el adulto en RT externa, pero el paciente
                pediátrico, la braquiterapia y la radiocirugía te siguen
                agarrando sin guión.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-teal-50 p-5 sm:p-8 rounded-2xl border border-teal-200" data-id="element-122">
              <h3 className="font-bold text-teal-700 mb-3 sm:mb-4 uppercase tracking-wider text-xs sm:text-sm" data-id="element-123">
                Con Megapack
              </h3>
              <p className="text-sm sm:text-base text-slate-900 font-medium" data-id="element-124">
                Guiones palabra por palabra para hablar con un niño de 4, de 8 o
                de 15 años. El banco de analogías que explica semillas,
                radiocirugía y SBRT en una frase. Los protocolos de las dos
                áreas donde la comunicación más falla y menos recursos existen.
              </p>
            </motion.div>
          </div>

          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 sm:mb-8 text-center" data-id="element-125">
            EL MEGAPACK INCLUYE:
          </h3>
          <div className="space-y-4 sm:space-y-6" data-id="element-126">
            <motion.div whileHover={{
            scale: 1.01
          }} className="bg-white border-2 border-amber-200 rounded-2xl p-5 sm:p-8 shadow-sm transition-shadow hover:shadow-md" data-id="element-127">
              <h4 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3 flex items-center gap-2" data-id="element-128">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-500 text-amber-500 shrink-0" data-id="element-129" />{' '}
                T7 — Radioterapia Pediátrica: Comunicación con el Niño y su
                Familia
              </h4>
              <p className="text-sm sm:text-base text-slate-600 pl-6 sm:pl-7" data-id="element-130">
                La tabla madre por edad, el protocolo de la máscara, la consulta
                con los padres y la hoja ilustrada para el niño con su
                calendario de stickers.
              </p>
            </motion.div>
            <motion.div whileHover={{
            scale: 1.01
          }} className="bg-white border-2 border-amber-200 rounded-2xl p-5 sm:p-8 shadow-sm transition-shadow hover:shadow-md" data-id="element-131">
              <h4 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3 flex items-center gap-2" data-id="element-132">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-500 text-amber-500 shrink-0" data-id="element-133" />{' '}
                T8 — Braquiterapia y Técnicas Avanzadas: Cómo Explicar lo que el
                Paciente No Puede Ver
              </h4>
              <p className="text-sm sm:text-base text-slate-600 pl-6 sm:pl-7" data-id="element-134">
                8 analogías probadas, guiones completos de braquiterapia
                ginecológica y prostática, hoja de precauciones post-implante y
                la frase anti-abandono entre fases.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-teal-900 text-white" data-id="element-135">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-136">
          <ShieldCheck className="w-12 h-12 sm:w-16 sm:h-16 text-teal-400 mx-auto mb-4 sm:mb-6" data-id="element-137" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3 sm:mb-4" data-id="element-138">
            7 DÍAS — Garantía
          </h2>
          <p className="text-xl sm:text-2xl font-medium text-teal-300 mb-4 sm:mb-6" data-id="element-139">
            7 días para probarlo o te devolvemos el 100%
          </p>
          <p className="text-base sm:text-lg text-teal-100/80 leading-relaxed" data-id="element-140">
            Abrís los tomos, usás las plantillas en tu próxima consulta. Si no
            es lo que esperabas: sin preguntas, sin formularios, sin drama.
          </p>
        </div>
      </section>
    </div>;
}